"use client";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { adminCommonConfigCreate } from "@/entities";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}
export function CommonConfigWriteModal({ isOpen, onClose }: Props) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      slug: "",
      table: "",
      name: "",
      desc: "",
      is_category: false,
      is_search: false,
      is_menu: false,
      list_count: 15,
      page_count: 4,
    },
  });

  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: adminCommonConfigCreate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-common-configs", {}],
      });
      toast.success("모듈이 생성되었습니다.");
      onClose();
    },
    onError: () => {
      toast.error("다시 시도해주세요");
    },
  });
  const onSubmit = async (data: any) => {
    if (createMutation.isPending) return;

    await createMutation.mutateAsync({ ...data });

    setValue("slug", "");
    setValue("table", "");
    setValue("name", "");
    setValue("desc", "");
    setValue("is_category", false);
    setValue("is_search", false);
    setValue("is_menu", false);
    setValue("list_count", 15);
    setValue("page_count", 4);
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader toggle={onClose}>모듈 생성</ModalHeader>
        <ModalBody className="p-0">
          <div className="form-horizontal form-bordered">
            <Controller
              name="slug"
              control={control}
              rules={{
                required: "슬러그를 입력해주세요.",
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="form-group row">
                  <label className="col-form-label col-md-3">슬러그</label>
                  <div className="col-md-9">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="슬러그(ex: notice, gallery...) 입력"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value || ""}
                    />

                    {errors.slug && (
                      <span className="text-red">
                        {errors.slug.message as string}
                      </span>
                    )}
                  </div>
                </div>
              )}
            />
            <Controller
              name="table"
              control={control}
              rules={{
                required: "테이블을 선택해주세요.",
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="form-group row">
                  <label className="col-form-label col-md-3">테이블</label>
                  <div className="col-md-9">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="테이블(ex: article, history...) 입력"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value || ""}
                    />

                    {errors.table && (
                      <span className="text-red">
                        {errors.table.message as string}
                      </span>
                    )}
                  </div>
                </div>
              )}
            />
            <Controller
              name="name"
              control={control}
              rules={{
                required: "이름을 입력해주세요.",
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="form-group row">
                  <label className="col-form-label col-md-3">이름</label>
                  <div className="col-md-9">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="이름 입력"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value || ""}
                    />
                    {errors.name && (
                      <span className="text-red">
                        {errors.name.message as string}
                      </span>
                    )}
                  </div>
                </div>
              )}
            />

            <Controller
              name="desc"
              control={control}
              rules={{
                required: "설명을 입력해주세요.",
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="form-group row">
                  <label className="col-form-label col-md-3">설명</label>
                  <div className="col-md-9">
                    <textarea
                      className="form-control"
                      placeholder="설명 입력"
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value || ""}
                    />
                    {errors.desc && (
                      <span className="text-red">
                        {errors.desc.message as string}
                      </span>
                    )}
                  </div>
                </div>
              )}
            />

            <div className="form-group row">
              <Controller
                name="is_category"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <label className="col-form-label col-md-3">카테고리</label>
                    <div className="col-md-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        onChange={onChange}
                        onBlur={onBlur}
                        checked={value}
                      />
                    </div>
                  </>
                )}
              />
            </div>
            <div className="form-group row hide">
              <Controller
                name="is_search"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <label className="col-form-label col-md-3">검색</label>
                    <div className="col-md-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        onChange={onChange}
                        onBlur={onBlur}
                        checked={value}
                      />
                    </div>
                  </>
                )}
              />
              <Controller
                name="is_menu"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <label className="col-form-label col-md-3">메뉴</label>
                    <div className="col-md-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        onChange={onChange}
                        onBlur={onBlur}
                        checked={value}
                      />
                    </div>
                  </>
                )}
              />
            </div>
            <div className="form-group row">
              <Controller
                name="list_count"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <label className="col-form-label col-md-3">
                      리스트 개수
                    </label>
                    <div className="col-md-3">
                      <input
                        type="number"
                        className="form-control"
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value || ""}
                      />
                    </div>
                    {errors.list_count && (
                      <span className="text-red">
                        {errors.list_count.message as string}
                      </span>
                    )}
                  </>
                )}
              />
              <Controller
                name="page_count"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <label className="col-form-label col-md-3">
                      페이지 개수
                    </label>
                    <div className="col-md-3">
                      <input
                        type="number"
                        className="form-control"
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value || ""}
                      />
                    </div>
                    {errors.page_count && (
                      <span className="text-red">
                        {errors.page_count.message as string}
                      </span>
                    )}
                  </>
                )}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button type="submit" className="btn btn-sm btn-inverse">
            <i className="fa fa-check"></i>
            <span> 저장</span>
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
